/**
 * Shared PDP buy box.
 * Pairs with snippets/rtnu-buybox.liquid and assets/rtnu-buybox.css.
 *
 * Modes (data-rtnu-buybox-mode on the root)
 *  - tiers:   one radio card per "Day Supply" plan; each sets the hidden
 *             selling_plan + quantity. "Buy Once" posts one unit to
 *             /cart/add.js and opens the Boost drawer.
 *  - classic: "Subscribe & Save" (with a delivery frequency <select>) vs
 *             "Buy Once" radio cards; one-time removes the selling_plan
 *             input's name so the form submits without a plan.
 *
 * Both modes re-price from the JSON map the snippet renders when the form's
 * variant input (name="id") changes, so no money formatting happens in JS.
 *
 * Events on the form
 *  - rtnu:buybox:change   { mode, planId, quantity, days, purchase }
 *  - rtnu:buybox:buy-once cart/add.js response (tiers mode only)
 */
(() => {
  if (window.__rtnuBuyboxBound) return;
  window.__rtnuBuyboxBound = true;

  const SEL = {
    root: '[data-rtnu-buybox]',
    plan: '[data-rtnu-buybox-plan]',
    input: '.rtnu-buybox__input',
    more: '.rtnu-buybox__more',
    select: '[data-rtnu-buybox-select]',
    pricing: '[data-rtnu-buybox-pricing]',
    sellingPlan: '[data-rtnu-selling-plan]',
    quantity: '[data-rtnu-quantity]',
    once: '[data-rtnu-buybox-once]',
    oncePrice: '[data-rtnu-buybox-once-price]',
    compare: '[data-rtnu-buybox-compare]',
    price: '[data-rtnu-buybox-price]',
    perDay: '[data-rtnu-buybox-per-day]',
    submit: '.rtnu-product__form-submit',
  };

  const parsePricing = (root) => {
    const node = root.querySelector(SEL.pricing);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.warn('[rtnu-buybox] Unable to parse pricing map', err);
      return null;
    }
  };

  const currentVariantId = (form) =>
    form.querySelector('input[name="id"]:checked')?.value ?? form.querySelector('input[name="id"]')?.value ?? null;

  const setText = (el, value) => {
    if (el && value != null) el.textContent = value;
  };

  const writePrices = (plan, data) => {
    setText(plan.querySelector(SEL.compare), data.compare);
    setText(plan.querySelector(SEL.price), data.total ?? data.price);
    setText(plan.querySelector(SEL.perDay), data.perDay);
  };

  const cartAddUrl = () => `${window.Shopify?.routes?.root ?? '/'}cart/add.js`;

  const initBuybox = (root) => {
    if (root.dataset.rtnuBuyboxReady) return;
    root.dataset.rtnuBuyboxReady = 'true';

    const form = root.closest('form');
    if (!form) return;

    const submitButton = form.querySelector(SEL.submit);
    const setCta = (text) => {
      if (submitButton?.type === 'submit') submitButton.textContent = text;
    };

    // Product without selling plans: plain one-time add.
    if (root.hasAttribute('data-rtnu-buybox-empty')) {
      setCta('Add to Cart');
      return;
    }

    const mode = root.dataset.rtnuBuyboxMode === 'classic' ? 'classic' : 'tiers';
    const plans = [...root.querySelectorAll(SEL.plan)];
    const sellingPlanInput = form.querySelector(SEL.sellingPlan);
    const quantityInput = form.querySelector(SEL.quantity);
    const onceButton = form.querySelector(SEL.once);
    const oncePrice = form.querySelector(SEL.oncePrice);
    const frequencySelect = root.querySelector(SEL.select);
    const pricing = parsePricing(root);

    const activePlan = () =>
      plans.find((plan) => !plan.hidden && plan.querySelector(SEL.input)?.checked) ?? null;

    const emitChange = (detail) =>
      form.dispatchEvent(new CustomEvent('rtnu:buybox:change', { bubbles: true, detail }));

    const setSubscription = (planId) => {
      if (!sellingPlanInput) return;
      sellingPlanInput.value = planId;
      sellingPlanInput.setAttribute('name', 'selling_plan');
    };

    const applyPlan = (plan, { silent = false } = {}) => {
      const input = plan?.querySelector(SEL.input);
      if (!input) return;

      plans.forEach((item) => {
        const active = item === plan;
        item.classList.toggle('is-active', active);
        // Tiers: collapsed benefit copy is visually clipped; keep it out of
        // the accessibility tree too. Classic cards never collapse, so their
        // content stays exposed.
        if (mode !== 'classic') {
          item.querySelectorAll(SEL.more).forEach((more) => more.setAttribute('aria-hidden', String(!active)));
        }
      });
      input.checked = true;

      const quantity = parseInt(input.dataset.quantity ?? '1', 10);
      if (quantityInput) quantityInput.value = String(quantity);

      const detail = { mode, quantity, purchase: 'subscription', planId: null };

      if (mode === 'classic' && input.value === 'onetime') {
        sellingPlanInput?.removeAttribute('name');
        detail.purchase = 'onetime';
        setCta('Add to Cart');
      } else if (mode === 'classic') {
        setSubscription(frequencySelect?.value ?? sellingPlanInput?.value ?? '');
        detail.planId = sellingPlanInput?.value ?? null;
        setCta('Subscribe Now');
      } else {
        setSubscription(input.value);
        detail.planId = input.value;
        detail.days = parseInt(input.dataset.days ?? '0', 10);
        setCta('Subscribe Now');
      }

      if (!silent) emitChange(detail);
    };

    const repriceForVariant = (variantId) => {
      const variant = pricing?.variants?.[variantId];
      if (!variant) return;

      plans.forEach((plan) => {
        const key = plan.dataset.rtnuBuyboxPlan;
        const data = mode === 'classic' ? variant[key] : variant.plans?.[key];
        const available = Boolean(data?.available);
        plan.hidden = !available;
        if (available) writePrices(plan, data);
      });

      setText(oncePrice, variant.onetime?.price ?? variant.onetime?.total);

      // If the selected option is not sold on this variant, fall back to the
      // first available one.
      if (!activePlan()) applyPlan(plans.find((plan) => !plan.hidden));
    };

    const buyOnce = async () => {
      const variantId = currentVariantId(form);
      if (!variantId || !onceButton) return;

      onceButton.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(cartAddUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
        });
        const data = await response.json();
        if (!response.ok) throw data;

        form.dispatchEvent(new CustomEvent('rtnu:buybox:buy-once', { bubbles: true, detail: data }));
        window.Boost?.Cart?.open?.();
      } catch (err) {
        console.error('[rtnu-buybox] Buy once failed', err);
        window.location.href = '/cart';
      } finally {
        onceButton.removeAttribute('aria-busy');
      }
    };

    plans.forEach((plan) => {
      plan.querySelector(SEL.input)?.addEventListener('change', (event) => {
        if (event.target.checked) applyPlan(plan);
      });
    });

    // Classic cards: the label only wraps the title row, so a tap anywhere
    // else on the card (benefits, empty space) should still select it.
    if (mode === 'classic') {
      plans.forEach((plan) => {
        plan.addEventListener('click', (event) => {
          if (event.target.closest('label, input, select, option, button, a')) return;
          const input = plan.querySelector(SEL.input);
          if (input && !input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    frequencySelect?.addEventListener('change', () => {
      if (!sellingPlanInput || activePlan()?.dataset.rtnuBuyboxPlan === 'onetime') return;
      sellingPlanInput.value = frequencySelect.value;
      emitChange({ mode, purchase: 'subscription', planId: frequencySelect.value, quantity: 1 });
    });

    form.addEventListener('change', (event) => {
      if (event.target?.name === 'id') repriceForVariant(event.target.value);
    });

    onceButton?.addEventListener('click', (event) => {
      event.preventDefault();
      buyOnce();
    });

    // Sync hidden inputs with the server-rendered default without firing
    // the change event.
    applyPlan(activePlan() ?? plans[0], { silent: true });
  };

  const initAll = () => document.querySelectorAll(SEL.root).forEach(initBuybox);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);

  // Apps and A/B tools can re-render the product form after load; pick up
  // any buy box that appears later.
  let pending = null;
  new MutationObserver(() => {
    if (pending) return;
    pending = requestAnimationFrame(() => {
      pending = null;
      initAll();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.rtnu = window.rtnu ?? {};
  window.rtnu.initBuybox = initAll;
})();
